import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

// Computes ISO date strings for built-in period presets
const presetRange = (preset) => {
  const today = new Date()
  const iso   = (d) => d.toISOString().slice(0, 10)
  const fromTo = (from) => ({ period_from: iso(from), period_to: iso(today) })
  switch (preset) {
    case "last_30_days": {
      const d = new Date(today); d.setDate(d.getDate() - 30); return fromTo(d)
    }
    case "last_90_days": {
      const d = new Date(today); d.setDate(d.getDate() - 90); return fromTo(d)
    }
    case "ytd": {
      return fromTo(new Date(today.getFullYear(), 0, 1))
    }
    case "last_12_months": {
      const d = new Date(today); d.setMonth(d.getMonth() - 12); return fromTo(d)
    }
    case "all_time": {
      return { period_from: "2020-01-01", period_to: iso(today) }
    }
    default: return null
  }
}

// Multi-select dropdown with checkboxes
export const MultiSelect = ({ label, icon, options, selected, onChange, placeholder }) => {
  const [open, setOpen]     = useState(false)
  const [query, setQuery]   = useState("")
  const safeOptions         = Array.isArray(options) ? options : []
  const selectedSet         = useMemo(() => new Set((selected || []).map(String)), [selected])
  const filtered            = useMemo(() => {
    if (!query.trim()) return safeOptions
    const q = query.toLowerCase()
    return safeOptions.filter(o => (o.name || o.label || "").toLowerCase().includes(q))
  }, [safeOptions, query])

  const toggle = (value) => {
    const v = String(value)
    const next = selectedSet.has(v)
      ? Array.from(selectedSet).filter(x => x !== v)
      : [...Array.from(selectedSet), v]
    onChange(next)
  }

  const clearAll = (e) => { e.stopPropagation(); onChange([]) }

  return (
    <div className="relative">
      <button
        type      = "button"
        onClick   = {() => setOpen(o => !o)}
        className = {`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-[12.5px] ${
          selectedSet.size > 0
            ? "border-indigo-300 bg-indigo-50 text-indigo-800"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        }`}
      >
        <FontAwesomeIcon icon={icon} className="w-3 h-3" />
        <span>{label}</span>
        {selectedSet.size > 0 && (
          <>
            <span className="px-1.5 py-0.5 text-[10.5px] font-semibold bg-white border border-indigo-200 text-indigo-700 rounded">
              {selectedSet.size}
            </span>
            <span onClick={clearAll} title={I18n.t("chantiers_dashboard.clear")}>
              <FontAwesomeIcon icon="xmark" className="w-2.5 h-2.5 ml-0.5 text-indigo-600 hover:text-indigo-900" />
            </span>
          </>
        )}
        <FontAwesomeIcon icon="chevron-down" className="w-2.5 h-2.5 ml-0.5" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 mt-1 w-72 max-h-80 overflow-hidden bg-white border border-slate-200 rounded-lg shadow-lg flex flex-col">
            <div className="p-2 border-b border-slate-100">
              <input
                type        = "search"
                value       = {query}
                onChange    = {e => setQuery(e.target.value)}
                placeholder = {placeholder || I18n.t("chantiers_dashboard.search_placeholder")}
                className   = "w-full h-8 px-2 text-[12.5px] border border-slate-200 rounded"
              />
            </div>
            <ul className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-4 text-center text-[12px] text-slate-500">
                  {I18n.t("chantiers_dashboard.no_match")}
                </li>
              ) : filtered.map(opt => {
                const value = opt.id ?? opt.value
                const text  = opt.name ?? opt.label
                const checked = selectedSet.has(String(value))
                return (
                  <li key={value}>
                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={checked} onChange={() => toggle(value)} className="h-4 w-4" />
                      <span className="text-[12.5px] text-slate-800">{text}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}

const FilterBar = ({ filters, filter_options, partner_filter, loading, onChange }) => {
  const update = (key, value) => onChange({ ...filters, [key]: value })

  if (!filter_options) return null

  return (
    <div className="bg-white border-b border-slate-200 px-7 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {partner_filter && (
          <>
            <select
              value     = {filters.partner || ""}
              onChange  = {e => update("partner", e.target.value)}
              title     = {partner_filter.label}
              className = "h-7 px-2 text-[12px] border border-slate-200 rounded bg-white text-slate-700"
            >
              {partner_filter.all && <option value="">{partner_filter.all_label}</option>}
              {partner_filter.options.map(entity => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </select>
            <div className="h-5 w-px bg-slate-200 mx-2" />
          </>
        )}

        {/* Period presets */}
        <div className="flex items-center gap-1 mr-3">
          {filter_options.period_presets.map(p => (
            <button
              key       = {p.value}
              type      = "button"
              onClick   = {() => onChange({ ...filters, ...presetRange(p.value) })}
              className = "px-2.5 py-1 text-[11.5px] text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-md"
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date range */}
        <div className="flex items-center gap-1">
          <input
            type      = "date"
            value     = {filters.period_from || ""}
            onChange  = {e => update("period_from", e.target.value)}
            className = "h-7 px-2 text-[12px] border border-slate-200 rounded"
          />
          <span className="text-slate-400 text-[12px]">→</span>
          <input
            type      = "date"
            value     = {filters.period_to || ""}
            onChange  = {e => update("period_to", e.target.value)}
            className = "h-7 px-2 text-[12px] border border-slate-200 rounded"
          />
        </div>

        <div className="h-5 w-px bg-slate-200 mx-2" />

        <MultiSelect
          label    = {I18n.t("chantiers_dashboard.filters.clients")}
          icon     = "user-circle"
          options  = {filter_options.clients}
          selected = {filters.client_ids}
          onChange = {(ids) => update("client_ids", ids)}
        />

        <MultiSelect
          label    = {I18n.t("chantiers_dashboard.filters.managers")}
          icon     = "user-tie"
          options  = {filter_options.managers}
          selected = {filters.manager_ids}
          onChange = {(ids) => update("manager_ids", ids)}
        />

        <MultiSelect
          label    = {I18n.t("chantiers_dashboard.filters.project_statuses")}
          icon     = "hard-hat"
          options  = {filter_options.project_statuses}
          selected = {filters.project_statuses}
          onChange = {(values) => update("project_statuses", values)}
        />

        <MultiSelect
          label    = {I18n.t("chantiers_dashboard.filters.quote_statuses")}
          icon     = "file-invoice-dollar"
          options  = {filter_options.quote_statuses}
          selected = {filters.quote_statuses}
          onChange = {(values) => update("quote_statuses", values)}
        />

        {loading && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-slate-500">
            <FontAwesomeIcon icon="spinner" spin /> {I18n.t("chantiers_dashboard.loading")}
          </span>
        )}
      </div>
    </div>
  )
}

export default FilterBar
